import React, { useState, useEffect } from 'react';
import gradeProgressionService from '../services/gradeProgressionService';
import { useAuth } from '../context/AuthContext';
import './GradeProgressionManagement.css';

const GradeProgressionManagement = () => {
  const { hasModulePermission } = useAuth();
  const canUpdate = hasModulePermission('GRADE_PROGRESSION', 'update');
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoting, setPromoting] = useState({});
  const [promotingAll, setPromotingAll] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    categorie: '',
    echellon: '',
    degreActuel: '',
    search: '',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadEligibleEmployees();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, eligibleEmployees]);

  const loadEligibleEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await gradeProgressionService.getEligibleEmployees();
      setEligibleEmployees(response.data);
      setFilteredEmployees(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des employés éligibles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...eligibleEmployees];

    // Filtre par catégorie
    if (filters.categorie) {
      filtered = filtered.filter(emp => 
        emp.categorie && emp.categorie.toString() === filters.categorie
      );
    }

    // Filtre par échelon
    if (filters.echellon) {
      filtered = filtered.filter(emp => 
        emp.echellon && emp.echellon.toString() === filters.echellon
      );
    }

    // Filtre par degré actuel
    if (filters.degreActuel) {
      filtered = filtered.filter(emp => 
        emp.degreActuel && emp.degreActuel.toString() === filters.degreActuel
      );
    }

    // Filtre par recherche (nom, matricule, CIN, CNSS)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(emp => {
        const employeeName = (emp.employeeName || '').toLowerCase();
        const matricule = (emp.matricule || '').toLowerCase();
        const cin = (emp.cin || '').toLowerCase();
        const cnss = (emp.cnssNumber || '').toLowerCase();
        return employeeName.includes(searchLower) || 
               matricule.includes(searchLower) || 
               cin.includes(searchLower) || 
               cnss.includes(searchLower);
      });
    }

    setFilteredEmployees(filtered);
    setCurrentPage(1); // Reset à la première page lors du filtrage
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePromote = async (employeeId, employeeName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir promouvoir ${employeeName} au degré supérieur ?`)) {
      return;
    }

    try {
      setPromoting(prev => ({ ...prev, [employeeId]: true }));
      await gradeProgressionService.promoteEmployee(employeeId);
      setSuccessMessage(`${employeeName} a été promu avec succès`);
      setTimeout(() => setSuccessMessage(null), 5000);
      await loadEligibleEmployees();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la promotion');
      console.error(err);
    } finally {
      setPromoting(prev => ({ ...prev, [employeeId]: false }));
    }
  };

  const handlePromoteAll = async () => {
    const count = filteredEmployees.length;
    if (!window.confirm(`Êtes-vous sûr de vouloir promouvoir ${count} employé(s) éligible(s) ?`)) {
      return;
    }

    try {
      setPromotingAll(true);
      const response = await gradeProgressionService.promoteAllEligible();
      const promotedCount = response.data.count || count;
      setSuccessMessage(`${promotedCount} employé(s) promu(s) avec succès`);
      setTimeout(() => setSuccessMessage(null), 5000);
      await loadEligibleEmployees();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la promotion automatique');
      console.error(err);
    } finally {
      setPromotingAll(false);
    }
  };

  const getCategorieLabel = (categorie) => {
    if (!categorie) return 'N/A';
    const cat = parseInt(categorie);
    if (cat >= 1 && cat <= 6) return `Ouvrier (${cat})`;
    if (cat >= 7 && cat <= 11) return `Maîtrise (${cat})`;
    if (cat >= 12 && cat <= 16) return `Cadre (${cat})`;
    return categorie;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="grade-progression-management">
        <div className="loading">Chargement des employés éligibles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="grade-progression-management">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="grade-progression-management">
      <div className="page-header">
        <h1>Gestion des Promotions de Degrés</h1>
        {canUpdate && filteredEmployees.length > 0 && (
          <button
            className="btn btn-primary btn-promote-all"
            onClick={handlePromoteAll}
            disabled={promotingAll}
          >
            {promotingAll ? 'Promotion en cours...' : `Promouvoir tous les éligibles (${filteredEmployees.length})`}
          </button>
        )}
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label>Recherche (nom, matricule, CIN, CNSS)</label>
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Rechercher un employé (nom, matricule, CIN, CNSS)..."
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Catégorie</label>
          <select
            name="categorie"
            value={filters.categorie}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Toutes</option>
            <optgroup label="Ouvriers">
              {[1, 2, 3, 4, 5, 6].map(cat => (
                <option key={cat} value={cat.toString()}>{cat}</option>
              ))}
            </optgroup>
            <optgroup label="Maîtrise">
              {[7, 8, 9, 10, 11].map(cat => (
                <option key={cat} value={cat.toString()}>{cat}</option>
              ))}
            </optgroup>
            <optgroup label="Cadres">
              {[12, 13, 14, 15, 16].map(cat => (
                <option key={cat} value={cat.toString()}>{cat}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div className="filter-group">
          <label>Échelon</label>
          <select
            name="echellon"
            value={filters.echellon}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Tous</option>
            {Array.from({ length: 16 }, (_, i) => (
              <option key={i + 1} value={(i + 1).toString()}>{i + 1}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Degré actuel</label>
          <select
            name="degreActuel"
            value={filters.degreActuel}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">Tous</option>
            {Array.from({ length: 15 }, (_, i) => (
              <option key={i + 1} value={(i + 1).toString()}>{i + 1}</option>
            ))}
          </select>
        </div>

        <button
          className="btn btn-secondary"
          onClick={() => {
            setFilters({ categorie: '', echellon: '', degreActuel: '', search: '' });
          }}
        >
          Réinitialiser
        </button>
      </div>

      <div className="stats-section">
        <div className="stat-card">
          <span className="stat-label">Total éligibles</span>
          <span className="stat-value">{eligibleEmployees.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Filtrés</span>
          <span className="stat-value">{filteredEmployees.length}</span>
        </div>
      </div>

      {filteredEmployees.length === 0 ? (
        <div className="no-results">
          Aucun employé éligible à la promotion trouvé.
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="progression-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Catégorie</th>
                  <th>Échelon</th>
                  <th>Degré Actuel</th>
                  <th>Durée Restante</th>
                  <th>Date Promotion</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((employee) => (
                  <tr key={employee.employeeId}>
                    <td>{employee.employeeName}</td>
                    <td>{getCategorieLabel(employee.categorie)}</td>
                    <td>{employee.echellon || 'N/A'}</td>
                    <td>
                      <span className="degre-badge">{employee.degreActuel || 1}</span>
                    </td>
                    <td>
                      <span className="duree-badge">
                        {employee.dureeRestante || 0} an(s)
                      </span>
                    </td>
                    <td>
                      {employee.datePromotionEligible ? (
                        <span className="date-promotion">
                          {new Date(employee.datePromotionEligible).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      {canUpdate && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handlePromote(employee.employeeId, employee.employeeName)}
                          disabled={promoting[employee.employeeId]}
                        >
                          {promoting[employee.employeeId] ? 'Promotion...' : 'Promouvoir'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-sm"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Précédent
              </button>
              <span className="page-info">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                className="btn btn-sm"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GradeProgressionManagement;

